import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/articles";
import { animeList } from "@/mocks/anime";
import { organizationSchema, articleSchema, breadcrumbSchema } from "@/lib/seoData";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const anime = animeList.find((a) => a.id === id);

  if (!article && !anime) {
    return (
      <Layout>
        <section className="bg-background-50 pt-32 pb-20 text-center">
          <h1 className="font-heading text-3xl text-foreground-950">内容未找到</h1>
          <p className="mt-3 text-foreground-600">抱歉，你访问的内容不存在或已被移除。</p>
          <Link
            to="/categories"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回全部动漫
          </Link>
        </section>
      </Layout>
    );
  }

  // 动漫详情视图
  if (anime && !article) {
    const related = animeList
      .filter((a) => a.id !== anime.id && a.category === anime.category)
      .slice(0, 3);
    const jsonLd = [
      organizationSchema,
      articleSchema({
        title: anime.title,
        description: anime.description,
        date: `${anime.year}-01-01`,
        author: "动漫网编辑部",
        image: anime.cover,
        path: `/article/${anime.id}`,
      }),
      breadcrumbSchema([
        { name: "首页", path: "/" },
        { name: "全部动漫", path: "/categories" },
        { name: anime.title, path: `/article/${anime.id}` },
      ]),
    ];

    return (
      <Layout>
        <Seo
          title={`${anime.title} - ${anime.region} ${anime.status} 动漫详情`}
          description={`${anime.title}：${anime.description} 在动漫网查看${anime.title}的${anime.region}信息、评分、标签与相关推荐。`}
          keywords={`动漫网,${anime.title},${anime.region}动漫,动漫网全部动漫列表,动漫网漫画分类大全`}
          canonical={`/article/${anime.id}`}
          jsonLd={jsonLd}
        />
        <section className="bg-foreground-950 pt-24 pb-14">
          <div className="mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "全部动漫", path: "/categories" },
                { label: anime.title },
              ]}
            />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr] lg:items-start">
              <div className="overflow-hidden rounded-lg border border-background-50/10">
                <img src={anime.cover} alt={`${anime.title} 封面`} title={`${anime.title}`} className="aspect-[3/4] w-full object-cover object-top" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-foreground-950">{anime.region}</span>
                  <span className="rounded-full bg-background-50/10 px-3 py-1 text-xs text-background-50">{anime.status}</span>
                  {anime.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-50/10 px-3 py-1 text-xs text-background-50/80">
                      {t}
                    </span>
                  ))}
                </div>
                <h1 className="mt-4 font-heading text-3xl text-background-50 md:text-5xl">{anime.title}</h1>
                <dl className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="rounded-lg bg-background-50/5 p-3">
                    <dt className="text-xs text-background-50/60">评分</dt>
                    <dd className="mt-1 flex items-center gap-1 font-heading text-xl text-accent-400">
                      <i className="ri-star-fill text-sm"></i>
                      {anime.rating.toFixed(1)}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-background-50/5 p-3">
                    <dt className="text-xs text-background-50/60">集数</dt>
                    <dd className="mt-1 font-heading text-xl text-background-50">{anime.episodes} 集</dd>
                  </div>
                  <div className="rounded-lg bg-background-50/5 p-3">
                    <dt className="text-xs text-background-50/60">地区</dt>
                    <dd className="mt-1 font-heading text-xl text-background-50">{anime.region}</dd>
                  </div>
                  <div className="rounded-lg bg-background-50/5 p-3">
                    <dt className="text-xs text-background-50/60">年份</dt>
                    <dd className="mt-1 font-heading text-xl text-background-50">{anime.year}</dd>
                  </div>
                </dl>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-background-50/85">{anime.description}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to={`/categories?cat=${anime.category}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
                  >
                    查看同分类
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/categories"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/25 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-background-50/10"
                  >
                    浏览全部动漫
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">同分类动漫推荐</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/article/${r.id}`}
                  className="group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50"
                >
                  <div className="aspect-[3/4] w-full overflow-hidden">
                    <img src={r.cover} alt={`${r.title} 封面`} title={`${r.title}`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-3">
                    <h3 className="truncate font-heading text-base text-foreground-950">{r.title}</h3>
                    <p className="mt-1 text-xs text-foreground-600">{r.rating.toFixed(1)} 分 · {r.status}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // 文章详情视图
  const current = article!;
  const relatedArticles = articles.filter((a) => a.id !== current.id).slice(0, 3);
  const jsonLd = [
    organizationSchema,
    articleSchema({
      title: current.title,
      description: current.summary,
      date: current.date,
      author: current.author,
      image: current.cover,
      path: `/article/${current.id}`,
    }),
    breadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "番剧资讯", path: "/articles" },
      { name: current.title, path: `/article/${current.id}` },
    ]),
  ];

  return (
    <Layout>
      <Seo
        title={`${current.title} - 动漫网番剧资讯`}
        description={current.summary}
        keywords={`动漫网,${current.title},动漫网番剧资讯,动漫网新番索引,动漫网漫画分类大全`}
        canonical={`/article/${current.id}`}
        jsonLd={jsonLd}
      />

      <article className="bg-background-50 pt-24 pb-14 md:pb-20">
        <div className="mx-auto px-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <Breadcrumb
              items={[
                { label: "番剧资讯", path: "/articles" },
                { label: current.title },
              ]}
            />
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">{current.category}</span>
              {current.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 md:text-4xl">{current.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="whitespace-nowrap">{current.author}</span>
              <span className="whitespace-nowrap">{current.date}</span>
              <span className="whitespace-nowrap">阅读约 {current.readTime} 分钟</span>
            </div>
            <div className="mt-6 overflow-hidden rounded-lg border border-background-200/70">
              <img src={current.cover} alt={`${current.title} 配图`} title={`${current.title} 文章配图`} className="aspect-[16/9] w-full object-cover object-top" />
            </div>
            <div className="mt-8 space-y-5">
              {current.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-10 rounded-lg border border-background-200/70 bg-background-100 p-5 text-center">
              <p className="text-sm text-foreground-700">想看更多动漫？去动漫网全部动漫列表看看吧。</p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                <Link
                  to="/categories"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                >
                  浏览全部动漫
                </Link>
                <Link
                  to="/categories?cat=xinfan"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600"
                >
                  查看新番索引
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">相关资讯</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedArticles.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50"
              >
                <div className="aspect-[16/10] w-full overflow-hidden">
                  <img src={a.cover} alt={`${a.title} 配图`} title={`${a.title} 文章`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 font-heading text-base text-foreground-950">{a.title}</h3>
                  <p className="mt-2 text-xs text-foreground-500">{a.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}